import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user: contextUser } = useAuth();

  // Load saved email on component mount
  React.useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Save or remove email based on Remember Me checkbox
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    const result = await login(email, password);
    
    if (result.success) {
      // Wait a moment for AuthContext to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the logged-in user data and token
      const token = localStorage.getItem('token');
      
      // The user might not be in localStorage yet, so we need to decode from token or make an API call
      // Let's make an API call to get the user data
      let userData = null;
      try {
        const userResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        userData = userResponse.data;
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
      
      // Check if user is a customer
      if (userData && userData.role === 'customer') {
        try {
          // Fetch customer's registered calendar
          const calendarResponse = await axios.get(
            `${API_BASE_URL}/calendar-registrations/my-calendar/details`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const calendar = calendarResponse.data.data?.calendar;
          
          if (calendar && calendar.symbolic_name) {
            // Customer has a calendar - redirect to it
            navigate(`/calendar/${calendar.symbolic_name}`);
            return;
          } else {
            // Calendar found but no symbolic name
            setError('Unable to find your calendar. Please contact support.');
            setLoading(false);
            return;
          }
        } catch (err) {
          // Customer not registered to any calendar
          if (err.response?.status === 404 || err.response?.data?.detail?.includes('not registered')) {
            setError('You are not registered to any calendar. Please use "Find a Calendar" to select a business and register.');
            setLoading(false);
            return;
          } else {
            // Other error
            console.error('Error fetching customer calendar:', err);
            setError('Unable to load your calendar. Please try again or use "Find a Calendar".');
            setLoading(false);
            return;
          }
        }
      }
      
      // Admin user - proceed to dashboard
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h1>
              <p className="text-gray-600">Admin Login to yiBooking</p>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                  placeholder="you@example.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                    Remember me
                  </label>
                </div>
                <Link to="/forgot-password" className="text-sm text-yellow-500 hover:text-yellow-600 font-medium">
                  Forgot password?
                </Link>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white font-semibold py-3 rounded-md transition-colors"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Want to offer your services? <Link to="/register" className="text-yellow-500 hover:text-yellow-600 font-semibold">Create Calendar Admin Account</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Login;